import React, { useState } from 'react';
import { Container, Paper, Typography, TextField, Button, Box, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import api from '../api';

function CreateDispute() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company: '',
    category: 'other',
    description: '',
    amount_claimed: '',
    company_email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'billing', label: 'Billing/Overcharge' },
    { value: 'service', label: 'Poor Service' },
    { value: 'product', label: 'Defective Product' },
    { value: 'cancellation', label: 'Cancellation/Refund' },
    { value: 'lost_item', label: 'Lost/Damaged Item' },
    { value: 'warranty', label: 'Warranty Issue' },
    { value: 'fraud', label: 'Fraud/Unauthorized Charge' },
    { value: 'other', label: 'Other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        amount_claimed: formData.amount_claimed ? parseFloat(formData.amount_claimed) : null,
      };
      
      const response = await api.post('/disputes/', payload);
      // Pass prediction data if available (Pro feature)
      navigate(`/dispute/${response.data.id}`, { 
        state: { prediction: response.data.prediction, showPrediction: true } 
      });
    } catch (err) {
      console.error('Dispute creation error:', err.response?.data);
      
      if (err.response?.status === 403) {
        setError('You\'ve reached your monthly limit (3 free disputes). Upgrade to Pro for 10 letters/month or Enterprise for unlimited.');
      } else if (err.response?.status === 500) {
        // Show the actual error message from the backend
        const errorMsg = err.response?.data?.message || err.response?.data?.error || 'AI generation failed';
        setError('⚠️ AI Error: ' + errorMsg);
      } else {
        setError(err.response?.data?.message || 'Failed to generate letter. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AutoAwesomeIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main', mr: { xs: 1.5, md: 2 } }} />
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>Create Your Dispute Letter</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
              Describe what happened and get a professional, law-citing letter in seconds
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Company Name"
            margin="normal"
            required
            placeholder="e.g., Amazon, Delta Airlines, Comcast"
            value={formData.company}
            onChange={(e) => setFormData({...formData, company: e.target.value})}
            disabled={loading}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Issue Category</InputLabel>
            <Select
              value={formData.category}
              label="Issue Category"
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              disabled={loading}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="What Happened?"
            margin="normal"
            required
            multiline
            rows={6}
            placeholder="Be specific: dates, order numbers, what you paid, what went wrong, etc. Example: 'On Nov 15, I ordered item #12345 for $49.99. It arrived damaged with a cracked screen. I contacted support on Nov 18 but got no response.'"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            helperText={`${formData.description.length} characters (minimum 20)`}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Amount You're Disputing (Optional)"
            margin="normal"
            type="number"
            placeholder="49.99"
            value={formData.amount_claimed}
            onChange={(e) => setFormData({...formData, amount_claimed: e.target.value})}
            InputProps={{
              startAdornment: '$',
            }}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Company Email (Optional)"
            margin="normal"
            type="email"
            placeholder="support@company.com"
            helperText="We'll include it in your letter for reference"
            value={formData.company_email}
            onChange={(e) => setFormData({...formData, company_email: e.target.value})}
            disabled={loading}
          />

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              disabled={loading || formData.description.length < 20}
            >
              {loading ? 'Generating AI Letter...' : 'Generate My Letter'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>

          {loading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Our AI is analyzing your case and researching relevant consumer protection laws...
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Tips */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
        <Typography variant="h6" gutterBottom>Tips for Best Results</Typography>
        <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
          <li>Include specific dates, order numbers, and amounts</li>
          <li>Describe what you've tried to resolve it already</li>
          <li>State clearly what you want (refund, replacement, compensation)</li>
          <li>Be factual - the AI will add the legal language and firm tone</li>
        </Typography>
      </Paper>
    </Container>
  );
}

export default CreateDispute;
